import React from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { Card, CardContent, Grid, IconButton } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import Select from '../../../../components/forms/Select'
import EquipmentTypeDto from '../dto/EquipmentTypeDto'
import { CardTitle } from '../../../../components/card/NewCard'
import { darkBlue, whiteColor } from '../../../../utils/constants/ColorTheme'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { getMatTypes } from '../../../../utils/MaterielUtils'
import { replace } from '../../../../utils/FormUtils'
import { hasValue } from '../../../../utils/NumberUtil'
import CentralTypePanel from '../../central/components/CentralTypePanel'
import SensorTypePanel from '../../sensor/components/SensorTypePanel'
import TelecomTypePanel from '../../telecom/components/TelecomTypePanel'
import PowerSupplyTypePanel from '../../powerSupply/components/PowerSupplyTypePanel'
import AntennaTypePanel from '../../antenna/components/AntennaTypePanel'

const EquipmentTypePanel = ({
    materielType = {},
    onChange = () => { },
    openPopin = () => { },
}) => {
    const {
        contributors,
        centralTypes,
        sensorTypes,
        telecomTypes,
        simTypes,
        powerSupplyTypes,
        antennaTypes,
        variousMaterielTypes,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        centralTypes: store.CentralReducer.centralTypes,
        sensorTypes: store.SensorReducer.sensorTypes,
        telecomTypes: store.TelecomReducer.telecomTypes,
        simTypes: store.SimReducer.simTypes,
        powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
        antennaTypes: store.AntennaReducer.antennaTypes,
        variousMaterielTypes: store.VariousMaterielReducer.variousMaterielTypes,
    }), shallowEqual)

    const findType = (types, id) => types.find(t => t.id === id)

    const sections = [{
        key: 'centralType',
        legend: i18n.central,
        label: i18n.centralType,
        types: centralTypes,
        detailPanel: id => <CentralTypePanel materielType={findType(centralTypes, id)} showContributorField={false} disabled />,
    }, {
        key: 'sensorType',
        legend: i18n.sensor,
        label: i18n.sensorType,
        types: sensorTypes,
        detailPanel: id => <SensorTypePanel materielType={findType(sensorTypes, id)} showContributorField={false} disabled />,
    }, {
        key: 'telecomType',
        legend: i18n.telecom,
        label: i18n.telecomType,
        types: telecomTypes,
        detailPanel: id => <TelecomTypePanel materielType={findType(telecomTypes, id)} showContributorField={false} disabled />,
    }, {
        key: 'simType',
        legend: i18n.sim,
        label: i18n.simType,
        types: simTypes,
    }, {
        key: 'powerSupplyType',
        legend: i18n.powerSupply,
        label: i18n.powerSupplyType,
        types: powerSupplyTypes,
        detailPanel: id => <PowerSupplyTypePanel materielType={findType(powerSupplyTypes, id)} showContributorField={false} disabled />,
    }, {
        key: 'antennaType',
        legend: i18n.antenna,
        label: i18n.antennaType,
        types: antennaTypes,
        detailPanel: id => <AntennaTypePanel materielType={findType(antennaTypes, id)} showContributorField={false} disabled />,
    }, {
        key: 'variousMaterielType',
        legend: i18n.variousMateriel,
        label: i18n.variousMaterielType,
        types: variousMaterielTypes,
    }]

    return (
        <Grid container spacing={1}>
            <Grid size={6}>
                <Select
                    options={contributors}
                    label={i18n.provider}
                    value={materielType.provider}
                    onChange={v => onChange({ provider: v })}
                    keyValue='id'
                    keyLabel='mnemonique'
                />
            </Grid>
            <Grid size={6}>
                <Select
                    options={contributors}
                    label={i18n.manufacturer}
                    value={materielType.manufacturerId}
                    onChange={v => onChange({ manufacturerId: v })}
                    keyValue='id'
                    keyLabel='mnemonique'
                />
            </Grid>
            <Grid size={12}>
                <Card>
                    <CardTitle
                        title={i18n.materiels}
                        color={darkBlue}
                        actions={[{ icon: 'add', tooltip: i18n.add, color: whiteColor, onClick: openPopin }]}
                    />
                    <CardContent>
                        <Grid container spacing={1}>
                            {sections.filter(({ key }) => materielType[key]?.length).map(({ key, legend, label, types, detailPanel }) => (
                                <Grid size={12} key={key}>
                                    <StyledFieldSet>
                                        <StyledLegend>{legend}</StyledLegend>
                                        <Grid container spacing={1}>
                                            {materielType[key].map((id, index) => (
                                                <Grid container size={12} spacing={1} alignItems='center' key={index}>
                                                    <Grid size={11}>
                                                        <Select
                                                            label={label}
                                                            options={getMatTypes(types, id)}
                                                            value={id}
                                                            onChange={newId => onChange({ [key]: replace(materielType[key], newId, index) })}
                                                        />
                                                    </Grid>
                                                    <Grid size={1}>
                                                        <IconButton onClick={() => onChange({ [key]: materielType[key].filter((_, i) => i !== index) })}>
                                                            <ClearIcon />
                                                        </IconButton>
                                                    </Grid>
                                                    {hasValue(id) && !!detailPanel && (
                                                        <Grid size={12}>
                                                            {detailPanel(id)}
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </StyledFieldSet>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    )
}

EquipmentTypePanel.propTypes = {
    materielType: PropTypes.instanceOf(EquipmentTypeDto),
    onChange: PropTypes.func,
    openPopin: PropTypes.func,
}

export default EquipmentTypePanel
